/**
 * Payroll in-page IA — four sections named for the questions people ask.
 *
 * Finance L1 stays one "Payroll" leaf (MIXED Finance is already at the
 * 10-leaf density ceiling). These four tabs replace the flat 10-item
 * payroll strip.
 *
 * Plan: docs/operations/ai/handoffs/payroll-programme-implementation-plan-2026-08-15.md:199-208
 */
import type { SubPageTab } from '@statura/ui';
import { resolveActiveTab } from '@statura/ui';

export const PAYROLL_NAV_SECTIONS_VERSION = '1.0.0';

export const PAYROLL_NAV_SECTION_TABS: ReadonlyArray<SubPageTab> = [
  {
    // /payroll is the fortnight landing. Without a tab pointing at it the page
    // is unreachable from the strip once you navigate to any sibling tab.
    id: 'fortnight',
    label: 'This fortnight',
    href: '/payroll',
  },
  {
    id: 'pay-period',
    label: 'Pay period',
    href: '/payroll/readiness',
    activeFor: ['/payroll/corrections', '/payroll/fixed-pay', '/payroll/manual-closures'],
  },
  {
    id: 'pay-run',
    label: 'Pay run',
    href: '/payroll/queue',
    activeFor: ['/payroll/pay-runs', '/payroll/timesheet'],
  },
  {
    id: 'handoff',
    label: 'Handoff',
    href: '/payroll/provider-handoff',
    activeFor: ['/payroll/reconciliation', '/payroll/agency-invoices'],
  },
  {
    id: 'pay-rules',
    label: 'Pay rules',
    href: '/settings/payroll/profile',
    activeFor: ['/settings/payroll/eba', '/payroll/policy'],
  },
];

export function payrollNavSectionIdForPath(pathname: string): string | null {
  const href = resolveActiveTab(PAYROLL_NAV_SECTION_TABS, pathname);
  if (!href) return null;
  return PAYROLL_NAV_SECTION_TABS.find((tab) => tab.href === href)?.id ?? null;
}

export function payrollFallbackCsvHref(payRunId: string): string {
  return `/api/payroll/pay-runs/${payRunId}/fallback-csv`;
}

/** New-payroll discovery never exposes old workflow destinations. */
export const NEW_PAYROLL_NAV_TABS: ReadonlyArray<SubPageTab> = [
  { id: 'fortnight', label: 'Payroll work', href: '/payroll' },
  {
    id: 'pay-period',
    label: 'Fix a timesheet',
    href: '/payroll/corrections',
  },
  { id: 'pay-run', label: 'Pay runs', href: '/payroll/pay-runs' },
  { id: 'pay-rules', label: 'Payroll settings', href: '/settings/payroll' },
];
